import { useState } from 'react';
import { createClaimMdAppeal } from '../services/api';
import './ClaimMdActions.css';

const toArray = (v) => (v == null ? [] : Array.isArray(v) ? v : [v]);

// Pull a readable error out of the server's wrapped Claim.MD failure.
function readError(err, fallback) {
  const body = err?.response?.data;
  const detailErr = body?.details?.error;
  const payerMsg = Array.isArray(detailErr)
    ? detailErr.map((e) => e?.error_mesg).filter(Boolean).join('; ')
    : detailErr?.error_mesg;
  return payerMsg || body?.message || body?.error || err?.message || fallback;
}

export default function AppealModal({ claim, onClose }) {
  const [form, setForm] = useState({
    contactName: '', contactTitle: '', contactEmail: '', contactPhone: '', contactFax: '',
  });
  const [phase, setPhase]     = useState('form'); // 'form' | 'result'
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(null);
  const [link, setLink]       = useState(null);

  const upd = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const handleSubmit = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await createClaimMdAppeal({
        claimId:      claim.claim_id ?? claim.id,
        contactName:  form.contactName.trim()  || undefined,
        contactTitle: form.contactTitle.trim() || undefined,
        contactEmail: form.contactEmail.trim() || undefined,
        contactPhone: form.contactPhone.trim() || undefined,
        contactFax:   form.contactFax.trim()   || undefined,
      });
      const url = toArray(res?.appeal?.link)[0]?.url || res?.appeal?.url || null;
      if (!url) {
        setError('Claim.MD did not return an appeal link. Please try again.');
        setLoading(false);
        return;
      }
      setLink(url);
      setPhase('result');
      // Open immediately so the hosted form isn't lost if the modal is closed.
      window.open(url, '_blank', 'noopener');
    } catch (err) {
      setError(readError(err, 'Failed to start the appeal. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="cml-backdrop" onMouseDown={(e) => { if (e.target === e.currentTarget && !loading) onClose(); }}>
      <div className="cml-modal">
        <div className="cml-header">
          <div className="cml-header-left">
            <div className="cml-header-icon">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3zM7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3" />
              </svg>
            </div>
            <div>
              <h2 className="cml-title">Appeal Claim</h2>
              <p className="cml-subtitle">{claim.claim_number}</p>
            </div>
          </div>
          <button className="cml-close" onClick={onClose} disabled={loading}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <div className="cml-body">
          {phase === 'form' ? (
            <>
              <p className="cml-hint">
                This starts an appeal with the payer through Claim.MD and opens a hosted form
                with options to submit electronically, fax, mail, or download. The contact
                details below are recommended but optional.
              </p>
              <div className="cml-section-label">Appeal Contact</div>
              <div className="cml-form-grid">
                <div className="cml-field">
                  <label className="cml-label">Name</label>
                  <input className="cml-input" value={form.contactName} onChange={upd('contactName')} placeholder="Who is filing the appeal" />
                </div>
                <div className="cml-field">
                  <label className="cml-label">Title</label>
                  <input className="cml-input" value={form.contactTitle} onChange={upd('contactTitle')} placeholder="e.g. Billing Manager" />
                </div>
                <div className="cml-field">
                  <label className="cml-label">Email</label>
                  <input className="cml-input" type="email" value={form.contactEmail} onChange={upd('contactEmail')} />
                </div>
                <div className="cml-field">
                  <label className="cml-label">Phone</label>
                  <input className="cml-input" type="tel" value={form.contactPhone} onChange={upd('contactPhone')} />
                </div>
                <div className="cml-field">
                  <label className="cml-label">Fax</label>
                  <input className="cml-input" type="tel" value={form.contactFax} onChange={upd('contactFax')} />
                </div>
              </div>
              {error && <div className="cml-error">{error}</div>}
            </>
          ) : (
            <div className="cml-result">
              <div className="cml-result-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
              </div>
              <p className="cml-result-title">Appeal started</p>
              <p className="cml-result-sub">
                The appeal form opened in a new tab. If it was blocked, use the button below.
              </p>
              <div className="cml-result-link">{link}</div>
            </div>
          )}
        </div>

        <div className="cml-footer">
          {phase === 'form' ? (
            <>
              <button className="cml-btn-cancel" onClick={onClose} disabled={loading}>Cancel</button>
              <button className="cml-btn-primary" onClick={handleSubmit} disabled={loading}>
                {loading ? (<><span className="cml-spinner" />Starting…</>) : 'Start Appeal'}
              </button>
            </>
          ) : (
            <>
              <a className="cml-btn-primary" href={link} target="_blank" rel="noopener noreferrer">Open Appeal Form</a>
              <button className="cml-btn-cancel" onClick={onClose}>Done</button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
